import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { t, font, Button, Input, Panel, Badge, Avatar, StatCard, ProgressBar } from "@realtyflows/ui";
import {
  getMyTeam, createTeam, createLiteTeam, addLiteSeat, inviteAgent, resendInvite, removeMember,
  cancelInvitation, getTeamStats, deleteTeam, deactivateAgent, activateAgent,
  getTeamUsage, getTeamBreakdown, getInvoices, getSigningHistory,
} from "../api";
import ConfirmModal from "../components/ConfirmModal";
import Alert from "../components/Alert";
import useResponsive from "../hooks/useResponsive";
import useScrollToAlert from "../hooks/useScrollToError";

export default function Team({ user, onUserUpdate }) {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "members";

  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [brokerageName, setBrokerageName] = useState("");
  const [selectedTeamPlan, setSelectedTeamPlan] = useState("team_starter");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("agent");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [error, setError] = useState("");
  const [action, setAction] = useState("");
  const errorRef = useScrollToAlert(error);

  // Signing Usage tab state
  const [teamUsage, setTeamUsage] = useState(null);

  // Billing tab state
  const [breakdown, setBreakdown] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [signingHistory, setSigningHistory] = useState([]);

  // Settings tab state
  const [editTeamName, setEditTeamName] = useState("");
  const [editBrokerage, setEditBrokerage] = useState("");

  const fetchTeam = useCallback(async () => {
    try {
      const data = await getMyTeam(user.id);
      setTeam(data.team);
      setMembers(data.members);
      setInvitations(data.invitations);
      if (data.team) {
        const s = await getTeamStats(data.team.id);
        setStats(s);
        setEditTeamName(data.team.name || "");
        setEditBrokerage(data.team.brokerage || "");
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [user.id]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  // Load tab-specific data
  useEffect(() => {
    if (!team) return;
    if (tab === "usage") {
      getTeamUsage(team.id).then(setTeamUsage).catch(() => {});
    } else if (tab === "billing") {
      getTeamBreakdown().then(setBreakdown).catch(() => {});
      getInvoices().then(d => setInvoices(d.invoices || [])).catch(() => {});
      getSigningHistory(team.id).then(d => setSigningHistory(d.transactions || [])).catch(() => {});
    }
  }, [tab, team]);

  const setTab = (key) => setSearchParams({ tab: key }, { replace: true });

  const handleCreateTeam = async () => {
    if (!teamName.trim()) return;
    setError("");
    try {
      const result = await createTeam({ name: teamName, brokerage: brokerageName, team_plan: selectedTeamPlan });
      if (result.checkoutUrl) { window.location.href = result.checkoutUrl; return; }
      const { team: newTeam, user: updatedUser } = result;
      setTeam(newTeam);
      setShowCreateTeam(false);
      if (onUserUpdate) onUserUpdate(updatedUser);
      fetchTeam();
    } catch (err) { setError(err.message); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setError("");
    try {
      await inviteAgent({ team_id: team.id, email: inviteEmail, role: inviteRole, invited_by: user.id });
      setInviteEmail("");
      setShowInvite(false);
      fetchTeam();
    } catch (err) { setError(err.message); }
  };

  const handleRemoveMember = async (memberId) => {
    try { await removeMember(memberId, team.id); fetchTeam(); } catch (err) { setError(err.message); }
  };

  const handleDeactivate = async (memberId) => {
    setAction(`deactivating-${memberId}`);
    try { await deactivateAgent(memberId, team.id); fetchTeam(); } catch (err) { setError(err.message); }
    setAction("");
  };

  const handleActivate = async (memberId) => {
    setAction(`activating-${memberId}`);
    try { await activateAgent(memberId, team.id); fetchTeam(); } catch (err) { setError(err.message); }
    setAction("");
  };

  const handleResendInvite = async (invId) => {
    setAction(`resending-${invId}`);
    try { await resendInvite(invId); fetchTeam(); } catch (err) { setError(err.message); }
    setAction("");
  };

  const handleCancelInvite = async (invId) => {
    try { await cancelInvitation(invId); fetchTeam(); } catch (err) { console.error(err); }
  };

  const handleDeleteTeam = async () => {
    try {
      const { user: updatedUser } = await deleteTeam(team.id, user.id);
      setTeam(null); setMembers([]); setInvitations([]); setStats(null);
      if (onUserUpdate) onUserUpdate(updatedUser);
    } catch (err) { setError(err.message); }
  };

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading...</div>;

  const isProPlan = user.plan === "pro" && user.subscription_status === "active";

  const handleCreateLiteTeam = async () => {
    setError("");
    setAction("creating-lite");
    try {
      await createLiteTeam();
      fetchTeam();
    } catch (err) { setError(err.message); }
    setAction("");
  };

  const handleAddLiteSeat = async () => {
    setError("");
    setAction("adding-seat");
    try {
      await addLiteSeat();
      fetchTeam();
    } catch (err) { setError(err.message); }
    setAction("");
  };

  // ─── No team: show create ───
  if (!team && user.role !== "agent") {
    return (
      <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 600, margin: "0 auto" }}>
        {/* Pro Lite Team option */}
        {isProPlan && (
          <Panel padding={28} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>{"\uD83D\uDC65"}</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Add Team Members</div>
                <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Pro plan — invite up to 2 agents at $29/mo each</div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: t.textSecondary, marginBottom: 16, lineHeight: 1.6, fontFamily: font.body }}>
              Add agents to share your workspace. Each agent manages their own clients independently. No full team dashboard — upgrade to a Team plan for advanced team features.
            </div>
            <Alert ref={errorRef} message={error} onDismiss={() => setError("")} style={{ marginBottom: 12 }} />
            <Button variant="primary" onClick={handleCreateLiteTeam} disabled={action === "creating-lite"}>
              {action === "creating-lite" ? "Setting up..." : "Add My First Team Member — $29/mo"}
            </Button>
          </Panel>
        )}

        <Panel padding={32} style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.2 }}>{"\u2B21"}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
            {isProPlan ? "Or Create a Full Team" : "Create Your Team"}
          </div>
          <div style={{ fontSize: 14, color: t.textMuted, marginBottom: 28, lineHeight: 1.6, fontFamily: font.body }}>
            {isProPlan
              ? "Need more than 2 agents? Team plans include a dashboard, client assignment, and up to 50 seats."
              : "As a brokerage or team manager, you can invite agents, assign clients, and track everyone's performance from one dashboard."
            }
          </div>
          {!showCreateTeam ? (
            <Button variant="primary" size="lg" onClick={() => setShowCreateTeam(true)}>
              {isProPlan ? "Upgrade to Team Plan" : "Create Team"}
            </Button>
          ) : (
            <div style={{ textAlign: "left" }}>
              <Alert ref={errorRef} message={error} onDismiss={() => setError("")} style={{ marginBottom: 16 }} />

              {/* Team Plan Selection */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: t.textSecondary, marginBottom: 8, fontFamily: font.body }}>Select Team Plan *</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {[
                    { key: "team_starter", name: "Team Starter", price: "$249/mo", seats: "5 users", signings: "20 signings" },
                    { key: "team_pro", name: "Team Pro", price: "$549/mo", seats: "15 users", signings: "50 signings" },
                    { key: "agency", name: "Agency", price: "$999/mo", seats: "50 users", signings: "100 signings" },
                  ].map(p => (
                    <div key={p.key} onClick={() => setSelectedTeamPlan(p.key)} style={{
                      padding: 12, borderRadius: 10, cursor: "pointer", textAlign: "center",
                      background: selectedTeamPlan === p.key ? t.emeraldLight : t.surfaceWarm,
                      border: selectedTeamPlan === p.key ? `2px solid ${t.emerald}` : `1px solid ${t.border}`,
                      transition: "all 0.15s",
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: selectedTeamPlan === p.key ? t.emerald : t.textPrimary, fontFamily: font.body }}>{p.name}</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: t.textPrimary, fontFamily: font.mono, margin: "4px 0" }}>{p.price}</div>
                      <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.body }}>{p.seats} · {p.signings}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: t.textMuted, marginTop: 6, fontFamily: font.body }}>
                  This is in addition to your solo plan. Extra seats: {selectedTeamPlan === "agency" ? "$19" : "$29"}/seat/month.
                </div>
              </div>

              <div style={{ marginBottom: 14 }}><Input label="Team Name *" value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Yilmaz Realty Group" /></div>
              <div style={{ marginBottom: 20 }}><Input label="Brokerage (optional)" value={brokerageName} onChange={e => setBrokerageName(e.target.value)} placeholder="ABC Realty LLC" /></div>
              <div style={{ display: "flex", gap: 10 }}>
                <Button variant="primary" onClick={handleCreateTeam}>Create & Subscribe</Button>
                <Button variant="secondary" onClick={() => setShowCreateTeam(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </Panel>
        <div style={{ background: t.emeraldLight, border: `1px solid ${t.emerald}20`, borderRadius: 12, padding: 20, marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.emerald, marginBottom: 8, fontFamily: font.body }}>Team Manager Plan Includes</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {["Unlimited clients", "AI client scoring", "Full email automations", "Invite unlimited agents", "Assign clients to agents", "Team performance dashboard", "Agent activity tracking", "Brokerage-wide pipeline"].map(f => (
              <div key={f} style={{ fontSize: 12, color: t.textPrimary, display: "flex", gap: 6, alignItems: "center", fontFamily: font.body }}><span style={{ color: t.emerald }}>{"\u2713"}</span> {f}</div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Agent view ───
  if (team && user.role === "agent") {
    return (
      <div style={{ animation: "fadeSlide 0.3s ease" }}>
        <Panel padding={24} style={{ maxWidth: 600 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Your Team</div>
          <div style={{ background: t.surfaceWarm, borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>{team.name}</div>
            {team.brokerage && <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>{team.brokerage}</div>}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 10, fontFamily: font.body }}>Team Members</div>
          {members.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${t.border}` }}>
              <Avatar name={m.name} size={32} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{m.name} {m.id === user.id && "(You)"}</div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{m.email}</div>
              </div>
              <Badge variant={m.role === "team_manager" ? "emerald" : "sky"}>{m.role === "team_manager" ? "Manager" : "Agent"}</Badge>
            </div>
          ))}
        </Panel>
      </div>
    );
  }

  // ─── Tabs config ───
  const tabs = isLiteTeam
    ? [{ key: "members", label: "Members" }]
    : [
        { key: "members", label: "Members" },
        { key: "usage", label: "Signing Usage" },
        { key: "billing", label: "Billing" },
        { key: "settings", label: "Settings" },
      ];

  const pendingInvites = invitations.filter(i => i.status === "pending");
  const rejectedInvites = invitations.filter(i => i.status === "rejected");
  const activeMembers = members.filter(m => m.is_active !== false);
  const isLiteTeam = team?.team_plan === "pro_lite";
  const includedSeats = team?.included_seats || (isLiteTeam ? 1 : 5);
  const teamExtraSeats = team?.extra_seats || 0;
  const totalSeats = includedSeats + teamExtraSeats;
  const extraSeats = Math.max(0, activeMembers.length - includedSeats);

  // ─── Team Manager view ───
  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Stats Row */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
          <StatCard label="Team Members" value={members.length.toString()} delay="0s" />
          <StatCard label="Total Clients" value={(stats.totalClients || 0).toString()} delay="0.07s" />
          <StatCard label="Total Deals" value={(stats.totalDeals || 0).toString()} delay="0.14s" />
          <StatCard label="Pending Invites" value={pendingInvites.length.toString()} delay="0.21s" />
        </div>
      )}

      {/* Tab Bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, background: t.pageBg, borderRadius: 10, padding: 3, border: `1px solid ${t.border}` }}>
        {tabs.map(tb => (
          <div key={tb.key} onClick={() => setTab(tb.key)} style={{
            flex: 1, padding: "10px 0", textAlign: "center", borderRadius: 8, fontSize: 12, fontWeight: 600,
            fontFamily: font.body, cursor: "pointer", transition: "all 0.2s",
            background: tab === tb.key ? t.surface : "transparent",
            color: tab === tb.key ? t.textPrimary : t.textMuted,
            boxShadow: tab === tb.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
          }}>
            {tb.label}
          </div>
        ))}
      </div>

      <Alert ref={errorRef} message={error} onDismiss={() => setError("")} style={{ marginBottom: 16 }} />

      {/* ═══════════════ MEMBERS TAB ═══════════════ */}
      {tab === "members" && (
        <div style={{ display: "grid", gridTemplateColumns: responsive.settingsColumns, gap: 20 }}>
          <Panel padding={24}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>{team.name}</div>
                {team.brokerage && <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>{team.brokerage}</div>}
              </div>
              <Button variant={showInvite ? "primary" : "outline"} size="sm" onClick={() => setShowInvite(!showInvite)}>+ Invite Member</Button>
            </div>

            {/* Seat counter */}
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: t.textSecondary, fontFamily: font.body }}>
                Seats {isLiteTeam && <span style={{ fontSize: 10, color: t.textMuted }}>(Pro — max {includedSeats + 2})</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, fontFamily: font.mono, color: extraSeats > 0 ? t.amber : t.textPrimary }}>
                  {activeMembers.length}/{totalSeats}
                  {teamExtraSeats > 0 && <span style={{ color: t.amber }}> ({teamExtraSeats} extra @ $29/mo)</span>}
                </div>
                {isLiteTeam && teamExtraSeats < 2 && activeMembers.length >= totalSeats && (
                  <button onClick={handleAddLiteSeat} disabled={action === "adding-seat"} style={{
                    padding: "4px 10px", borderRadius: 6, border: "none", fontSize: 10, fontWeight: 700,
                    background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`, color: "#fff",
                    cursor: action === "adding-seat" ? "not-allowed" : "pointer", fontFamily: font.body,
                  }}>
                    {action === "adding-seat" ? "..." : "+Seat $29/mo"}
                  </button>
                )}
              </div>
            </div>

            {/* Invite Form */}
            {showInvite && (
              <div style={{ background: t.surfaceWarm, border: `1.5px solid ${t.emerald}30`, borderRadius: 10, padding: 16, marginBottom: 16, animation: "scaleIn 0.2s ease" }}>
                <div style={{ marginBottom: 10 }}><Input label="Agent Email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="agent@company.com" /></div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Role</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["agent", "team_manager"].map(r => (
                      <div key={r} onClick={() => setInviteRole(r)} style={{
                        padding: "6px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                        background: inviteRole === r ? t.emeraldLight : t.surface, border: `1px solid ${inviteRole === r ? t.emerald : t.border}`,
                        color: inviteRole === r ? t.emerald : t.textMuted, fontWeight: 600, fontFamily: font.body,
                      }}>
                        {r === "agent" ? "Agent" : "Manager"}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="primary" size="sm" onClick={handleInvite}>Send Invite</Button>
                  <Button variant="secondary" size="sm" onClick={() => { setShowInvite(false); setError(""); }}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Members Table Header */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", padding: "8px 0", borderBottom: `1px solid ${t.border}`, fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>
              <div>NAME</div><div>ROLE</div><div>STATUS</div><div style={{ width: 60 }}>ACTIONS</div>
            </div>

            {/* Members List */}
            {members.map(m => (
              <div key={m.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${t.border}`, borderRadius: 6, transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => navigate(`/team/agent/${m.id}`)}>
                  <Avatar name={m.name} size={32} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: m.is_active === false ? t.textMuted : t.textPrimary, fontFamily: font.body }}>
                      {m.name} {m.id === user.id && "(You)"}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{m.email}</div>
                  </div>
                </div>
                <div><Badge variant={m.role === "team_manager" ? "emerald" : "sky"}>{m.role === "team_manager" ? "Manager" : "Agent"}</Badge></div>
                <div>
                  {m.is_active === false ? (
                    <span style={{ fontSize: 11, fontWeight: 600, color: t.coral, fontFamily: font.body }}>Deactivated</span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 600, color: t.emerald, fontFamily: font.body }}>Active</span>
                  )}
                </div>
                <div style={{ width: 60, display: "flex", justifyContent: "flex-end" }}>
                  {m.id !== user.id && (
                    <div style={{ position: "relative" }}>
                      <div onClick={() => setOpenMenu(openMenu === m.id ? null : m.id)} style={{
                        width: 26, height: 26, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", background: openMenu === m.id ? t.surfaceWarm : "transparent",
                        border: `1px solid ${openMenu === m.id ? t.border : "transparent"}`, transition: "all 0.15s",
                      }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="3" r="1.5" fill={t.textSecondary} /><circle cx="8" cy="8" r="1.5" fill={t.textSecondary} /><circle cx="8" cy="13" r="1.5" fill={t.textSecondary} /></svg>
                      </div>
                      {openMenu === m.id && (
                        <>
                          <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setOpenMenu(null)} />
                          <div style={{ position: "absolute", top: 30, right: 0, background: t.surface, border: `1.5px solid ${t.border}`, borderRadius: 10, padding: "6px 0", minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 100, animation: "scaleIn 0.15s ease" }}>
                            {m.is_active !== false ? (
                              <div onClick={() => { handleDeactivate(m.id); setOpenMenu(null); }} style={{ padding: "9px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, fontFamily: font.body, color: t.amber, transition: "background 0.1s" }}
                                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                <span style={{ fontSize: 13 }}>&#9888;</span><span>{action === `deactivating-${m.id}` ? "Deactivating..." : "Deactivate Agent"}</span>
                              </div>
                            ) : (
                              <div onClick={() => { handleActivate(m.id); setOpenMenu(null); }} style={{ padding: "9px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, fontFamily: font.body, color: t.emerald, transition: "background 0.1s" }}
                                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                <span style={{ fontSize: 13 }}>&#10003;</span><span>{action === `activating-${m.id}` ? "Activating..." : "Reactivate Agent"}</span>
                              </div>
                            )}
                            <div onClick={() => { setConfirmDelete({ type: "member", id: m.id, name: m.name }); setOpenMenu(null); }} style={{ padding: "9px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, fontFamily: font.body, color: t.coral, transition: "background 0.1s" }}
                              onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              <span style={{ fontSize: 13 }}>&#10005;</span><span>Remove from Team</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Pending Invitations */}
            {pendingInvites.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 8, fontFamily: font.mono, letterSpacing: 1 }}>PENDING INVITATIONS</div>
                {pendingInvites.map(inv => (
                  <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: t.amberLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>{"\u2709"}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: t.textPrimary, fontFamily: font.body }}>{inv.email}</div>
                      <div style={{ fontSize: 10, color: t.amber, fontFamily: font.body }}>Pending &middot; {new Date(inv.created_at).toLocaleDateString()}</div>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => handleResendInvite(inv.id)} disabled={action === `resending-${inv.id}`}>
                      {action === `resending-${inv.id}` ? "Sending..." : "Resend"}
                    </Button>
                    <div onClick={() => handleCancelInvite(inv.id)} style={{ cursor: "pointer", padding: 4, color: t.textMuted, fontSize: 14 }}>{"\u2715"}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Rejected Invitations */}
            {rejectedInvites.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 8, fontFamily: font.mono, letterSpacing: 1 }}>DECLINED INVITATIONS</div>
                {rejectedInvites.map(inv => (
                  <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: t.coralLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: t.coral }}>{"\u2715"}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body }}>{inv.email}</div>
                      <div style={{ fontSize: 10, color: t.coral, fontFamily: font.body }}>Declined &middot; {inv.rejected_at ? new Date(inv.rejected_at).toLocaleDateString() : ""}</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleResendInvite(inv.id)} disabled={action === `resending-${inv.id}`}>
                      {action === `resending-${inv.id}` ? "Sending..." : "Resend Invite"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* Agent Performance */}
          <Panel padding={24}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 20, fontFamily: font.display }}>Agent Performance</div>
            {stats && stats.agents.length > 0 ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", padding: "8px 0", borderBottom: `1px solid ${t.border}`, fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>
                  <div>AGENT</div><div>CLIENTS</div><div>DEALS</div><div>CLOSED</div>
                </div>
                {stats.agents.map(a => {
                  const maxClients = Math.max(...stats.agents.map(ag => ag.clients), 1);
                  return (
                    <div key={a.id} onClick={() => navigate(`/team/agent/${a.id}`)} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${t.border}`, cursor: "pointer", borderRadius: 8, transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar name={a.name} size={28} />
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: a.is_active === false ? t.textMuted : t.textPrimary, fontFamily: font.body }}>{a.name}</span>
                          {a.is_active === false && <span style={{ fontSize: 9, color: t.coral, marginLeft: 6, fontFamily: font.mono }}>DEACTIVATED</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.mono }}>{a.clients}</div>
                        <div style={{ width: 60, marginTop: 4 }}><ProgressBar value={(a.clients / maxClients) * 100} color={t.emerald} size="sm" /></div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.mono }}>{a.deals}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: a.closed > 0 ? t.emerald : t.textMuted, fontFamily: font.mono }}>{a.closed}</div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0", color: t.textMuted, fontSize: 13, fontFamily: font.body }}>
                Assign clients to agents to see performance data
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* ═══════════════ SIGNING USAGE TAB ═══════════════ */}
      {tab === "usage" && (
        <div>
          {/* Balance Overview */}
          {teamUsage && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "1fr" : "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
                <Panel padding={20}>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono, letterSpacing: 0.5, marginBottom: 6 }}>PLAN SIGNINGS</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: t.textPrimary, fontFamily: font.mono }}>{teamUsage.planSignings}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{teamUsage.planName} plan &middot; monthly</div>
                </Panel>
                <Panel padding={20}>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono, letterSpacing: 0.5, marginBottom: 6 }}>ADD-ON BALANCE</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: t.emerald, fontFamily: font.mono }}>{teamUsage.addonBalance}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>Never expires &middot; rolls over</div>
                </Panel>
                <Panel padding={20}>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono, letterSpacing: 0.5, marginBottom: 6 }}>USED THIS MONTH</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: teamUsage.totalUsed >= teamUsage.planSignings ? t.coral : t.textPrimary, fontFamily: font.mono }}>{teamUsage.totalUsed}</div>
                  <div style={{ marginTop: 6 }}><ProgressBar value={Math.min(100, (teamUsage.totalUsed / Math.max(1, teamUsage.totalAvailable)) * 100)} color={teamUsage.totalUsed >= teamUsage.planSignings ? t.coral : t.emerald} size="sm" /></div>
                </Panel>
              </div>

              {/* Buy More Button */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
                <Button variant="outline" size="sm" onClick={() => navigate("/billing?tab=plans")}>Buy More Signings</Button>
              </div>

              {/* Per-Member Usage Table */}
              <Panel padding={24}>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Per-Member Usage This Month</div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", padding: "8px 0", borderBottom: `1px solid ${t.border}`, fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>
                  <div>MEMBER</div><div>SIGNINGS USED</div><div>ADD-ON BALANCE</div><div>STATUS</div>
                </div>
                {teamUsage.members.map(m => (
                  <div key={m.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar name={m.name} size={28} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{m.name}</div>
                        <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{m.email}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, fontFamily: font.mono, color: t.textPrimary }}>{m.signingsUsed}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, fontFamily: font.mono, color: m.addonBalance > 0 ? t.emerald : t.textMuted }}>{m.addonBalance}</div>
                    <div>
                      {m.is_active === false
                        ? <span style={{ fontSize: 11, fontWeight: 600, color: t.coral, fontFamily: font.body }}>Deactivated</span>
                        : <span style={{ fontSize: 11, fontWeight: 600, color: t.emerald, fontFamily: font.body }}>Active</span>}
                    </div>
                  </div>
                ))}
              </Panel>
            </>
          )}
          {!teamUsage && <div style={{ textAlign: "center", padding: 40, color: t.textMuted, fontFamily: font.body }}>Loading usage data...</div>}
        </div>
      )}

      {/* ═══════════════ BILLING TAB ═══════════════ */}
      {tab === "billing" && (
        <div style={{ display: "grid", gridTemplateColumns: responsive.settingsColumns, gap: 20 }}>
          {/* Billing Breakdown */}
          <Panel padding={24}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 20, fontFamily: font.display }}>Monthly Breakdown</div>
            {breakdown ? (
              <>
                {breakdown.lineItems.map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${t.border}` }}>
                    <div>
                      <div style={{ fontSize: 14, color: t.textPrimary, fontFamily: font.body }}>{item.label}</div>
                      <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                        {item.type === "plan" && "Base subscription"}
                        {item.type === "seats" && `${breakdown.totalMembers} members, ${breakdown.includedSeats} included`}
                        {item.type === "addons" && "One-time purchases"}
                      </div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, fontFamily: font.mono, color: t.textPrimary }}>${(item.amount / 100).toLocaleString()}</div>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0", marginTop: 4 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>Total</div>
                  <div style={{ fontSize: 22, fontWeight: 800, fontFamily: font.mono, color: t.emerald }}>{breakdown.totalFormatted}/mo</div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: 20, color: t.textMuted, fontFamily: font.body }}>Loading...</div>
            )}
          </Panel>

          {/* Invoice History */}
          <Panel padding={24}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 20, fontFamily: font.display }}>Invoice History</div>
            {invoices.length > 0 ? invoices.slice(0, 10).map(inv => (
              <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${t.border}` }}>
                <div>
                  <div style={{ fontSize: 13, color: t.textPrimary, fontFamily: font.body }}>{inv.number || "Invoice"}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{new Date(inv.date).toLocaleDateString()}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, fontFamily: font.mono }}>${inv.amount}</div>
                  <Badge variant={inv.status === "paid" ? "emerald" : "amber"}>{inv.status}</Badge>
                  {inv.hostedUrl && <a href={inv.hostedUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: t.emerald, fontFamily: font.body }}>View</a>}
                </div>
              </div>
            )) : (
              <div style={{ textAlign: "center", padding: 20, color: t.textMuted, fontSize: 13, fontFamily: font.body }}>No invoices yet</div>
            )}

            {/* Signing Transaction History */}
            {signingHistory.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>Signing Transactions</div>
                {signingHistory.slice(0, 8).map(tx => (
                  <div key={tx.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {tx.user_avatar && <Avatar name={tx.user_name} size={22} />}
                      <div>
                        <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{tx.user_name || "System"}</div>
                        <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>
                          {tx.type === "addon_purchase" ? "Purchased add-on" : tx.type === "signing_used" ? "Signing used" : tx.type}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: font.mono, color: tx.type === "addon_purchase" ? t.emerald : t.textPrimary }}>
                        {tx.type === "addon_purchase" ? "+" : "-"}{tx.quantity}
                      </div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{new Date(tx.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* ═══════════════ SETTINGS TAB ═══════════════ */}
      {tab === "settings" && (
        <div style={{ maxWidth: 600 }}>
          <Panel padding={24}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 20, fontFamily: font.display }}>Team Settings</div>
            <div style={{ marginBottom: 14 }}>
              <Input label="Team Name" value={editTeamName} onChange={e => setEditTeamName(e.target.value)} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <Input label="Brokerage" value={editBrokerage} onChange={e => setEditBrokerage(e.target.value)} />
            </div>
            <Button variant="primary" size="sm" disabled>Save Changes</Button>

            {/* Danger Zone */}
            <div style={{ marginTop: 40, paddingTop: 20, borderTop: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.coral, marginBottom: 8, fontFamily: font.display }}>Danger Zone</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 16, lineHeight: 1.6, fontFamily: font.body }}>
                Deleting the team will remove all members, unassign all team clients, and cannot be undone. Members keep their personal clients.
              </div>
              <Button variant="outline" size="sm" onClick={() => setConfirmDelete({ type: "team" })} style={{ borderColor: t.coral, color: t.coral }}>Delete Team</Button>
            </div>
          </Panel>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.type === "team" ? "Delete Team" : "Remove Team Member"}
          message={confirmDelete.type === "team"
            ? `Are you sure you want to delete "${team.name}"? All members will be removed and team clients will be unassigned. Personal clients are not affected. This cannot be undone.`
            : `Are you sure you want to remove "${confirmDelete.name}" from your team? Their team clients will be unassigned, but personal clients will remain with them.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            if (confirmDelete.type === "team") handleDeleteTeam();
            else handleRemoveMember(confirmDelete.id);
            setConfirmDelete(null);
          }}
        />
      )}
    </div>
  );
}
